import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import {
  deleteAgent,
  getAgentStats,
  listRuns,
  updateAgent,
  type Agent,
  type AgentRun,
} from './api';
import { NewTaskDialog } from './NewTaskDialog';

const TEMPLATE_LABEL: Record<string, string> = {
  payment: 'Payment',
  trading: 'Trading',
  compliance: 'Compliance',
  generic: 'Generic',
};

const TEMPLATE_TONE: Record<string, string> = {
  payment: 'bg-bit2me/10 text-bit2me border-bit2me/30',
  trading: 'bg-amber-50 text-amber-700 border-amber-200',
  compliance: 'bg-purple-50 text-purple-700 border-purple-200',
  generic: 'bg-surface-soft text-ink-tertiary border-line',
};

type Props = {
  orgId: string;
  agent: Agent;
};

export function AgentConsoleHeader({ orgId, agent }: Props) {
  const [runOpen, setRunOpen] = useState(false);
  const qc = useQueryClient();
  const nav = useNavigate();

  const runs = useQuery({
    queryKey: ['runs', orgId, agent.id],
    queryFn: () => listRuns(orgId, agent.id),
    enabled: !!orgId,
    refetchInterval: 5_000,
  });
  const stats = useQuery({
    queryKey: ['agent-stats', orgId, agent.id, 14],
    queryFn: () => getAgentStats(orgId, agent.id, 14),
    enabled: !!orgId,
    refetchInterval: 30_000,
  });

  const toggle = useMutation({
    mutationFn: () =>
      updateAgent(orgId, agent.id, {
        status: agent.status === 'paused' ? 'active' : 'paused',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent', orgId, agent.id] }),
  });

  // Archive is a soft delete: the backend flips status to "archived" and
  // the list view hides those by default. Hard delete isn't exposed —
  // we keep the row so the audit trail (runs, tasks, signed txs) stays
  // queryable.
  const archive = useMutation({
    mutationFn: () => deleteAgent(orgId, agent.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents', orgId] });
      nav('/agents');
    },
  });

  const activeRuns = useMemo(
    () => (runs.data ?? []).filter((r) => r.status === 'pending' || r.status === 'running').length,
    [runs.data],
  );
  const lastActivity = useMemo(() => latestActivity(runs.data), [runs.data]);

  const template = TEMPLATE_LABEL[agent.template_slug] ?? agent.template_slug;
  const templateTone = TEMPLATE_TONE[agent.template_slug] ?? TEMPLATE_TONE.generic;
  const isPaused = agent.status === 'paused';

  return (
    <div className="sticky top-0 z-20 -mx-6 -mt-6 mb-2 border-b border-line-soft bg-surface-white/95 px-6 pb-4 pt-6 backdrop-blur">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-eyebrow ${templateTone}`}
            >
              {template}
            </span>
            <StatusPill status={agent.status} active={activeRuns > 0} />
          </div>
          <h1 className="mt-1 truncate text-2xl font-semibold tracking-tightish text-ink-primary">
            {agent.name}
          </h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => toggle.mutate()}
            disabled={toggle.isPending || agent.status === 'archived'}
          >
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
          <Button onClick={() => setRunOpen(true)} disabled={isPaused}>
            + New task
          </Button>
          {agent.status !== 'archived' && (
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Archive "${agent.name}"? Pending tasks will stop firing and the agent will disappear from the list. Past runs stay queryable.`,
                  )
                ) {
                  archive.mutate();
                }
              }}
              disabled={archive.isPending}
              className="rounded-md border border-danger/30 px-3 py-1.5 text-xs font-medium text-danger hover:bg-danger/5 disabled:opacity-50"
            >
              {archive.isPending ? 'Archiving…' : 'Archive'}
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Today"
          value={
            stats.data
              ? `${stats.data.runs_today} / ${stats.data.max_runs_per_day || '∞'}`
              : '—'
          }
          hint="runs"
          tone={quotaTone(stats.data?.runs_today, stats.data?.max_runs_per_day)}
        />
        <Stat
          label="Active"
          value={String(activeRuns)}
          hint="in flight"
          tone={activeRuns > 0 ? 'text-amber-600' : ''}
        />
        <Stat
          label="Last activity"
          value={lastActivity ? timeAgo(lastActivity) : '—'}
        />
        <Stat
          label="Wallet"
          value={agent.wallet_id ? shortenAddress(agent.wallet_id) : 'none'}
          mono
        />
      </div>

      <NewTaskDialog open={runOpen} onClose={() => setRunOpen(false)} orgId={orgId} agentId={agent.id} />
    </div>
  );
}

function StatusPill({ status, active }: { status: string; active: boolean }) {
  const tone =
    status === 'active'
      ? 'bg-ok/10 text-ok border-ok/30'
      : status === 'paused'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-surface-soft text-ink-tertiary border-line';
  const label = active ? 'running' : status;
  return (
    <span
      className={`flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${tone}`}
    >
      {active && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
      {label}
    </span>
  );
}

function Stat({
  label,
  value,
  hint,
  mono = false,
  tone = '',
}: {
  label: string;
  value: string;
  hint?: string;
  mono?: boolean;
  tone?: string;
}) {
  return (
    <div className="rounded-lg border border-line-soft bg-surface-soft/30 px-3 py-2">
      <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">{label}</p>
      <p
        className={`mt-0.5 text-base font-semibold ${mono ? 'font-mono text-sm' : ''} ${
          tone || 'text-ink-primary'
        }`}
      >
        {value}
      </p>
      {hint && <p className="font-mono text-[10px] text-ink-tertiary">{hint}</p>}
    </div>
  );
}

function quotaTone(today?: number, cap?: number): string {
  if (!cap || cap === 0) return '';
  const pct = (today ?? 0) / cap;
  if (pct >= 0.9) return 'text-danger';
  if (pct >= 0.7) return 'text-amber-600';
  return '';
}

function latestActivity(runs?: AgentRun[]): string | undefined {
  if (!runs || runs.length === 0) return undefined;
  return runs[0].created_at;
}

function shortenAddress(a: string): string {
  return a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
